/**
 * Pricing maths. Money is always integer **minor units** (paise); percentage discounts are stored
 * in **basis points** (1000 = 10 %). The same functions run in the API (authoritative) and in the UI
 * (previews in the admin discount editor), so both always agree.
 */
import type { DiscountType, PriceUnit } from '../constants/enums';

export interface DiscountInput {
  type: DiscountType;
  /** PERCENTAGE: basis points 1…10000 · FLAT: minor units > 0 */
  value: number;
  label?: string | null;
  startsAt?: Date | string | null;
  endsAt?: Date | string | null;
  isActive?: boolean;
}

export interface PriceBreakdown {
  unit: PriceUnit;
  currency: string;
  /** null ⇒ price on request */
  baseMinor: number | null;
  /** base minus the best valid discount; equals base when none applies */
  finalMinor: number | null;
  discount: {
    type: DiscountType;
    value: number;
    label: string | null;
    endsAt: string | null;
    /** whole-percent off for badges (rounded) */
    percentOff: number;
  } | null;
}

const toTime = (value: Date | string | null | undefined): number | null =>
  value === null || value === undefined ? null : new Date(value).getTime();

export function isDiscountActive(discount: DiscountInput, now: Date = new Date()): boolean {
  if (discount.isActive === false) return false;
  const t = now.getTime();
  const starts = toTime(discount.startsAt);
  const ends = toTime(discount.endsAt);
  if (starts !== null && t < starts) return false;
  if (ends !== null && t >= ends) return false;
  return true;
}

/** Price after one discount, never below zero, rounded to whole minor units. */
export function applyDiscount(
  baseMinor: number,
  discount: Pick<DiscountInput, 'type' | 'value'>,
): number {
  const off =
    discount.type === 'PERCENTAGE'
      ? Math.round((baseMinor * Math.min(discount.value, 10_000)) / 10_000)
      : discount.value;
  return Math.max(0, baseMinor - off);
}

export function percentOff(baseMinor: number, finalMinor: number): number {
  if (baseMinor <= 0 || finalMinor >= baseMinor) return 0;
  return Math.round(((baseMinor - finalMinor) / baseMinor) * 100);
}

/** The valid discount giving the lowest final price (ties: the larger saving, then the later end date). */
export function bestDiscount(
  baseMinor: number,
  discounts: readonly DiscountInput[],
  now: Date = new Date(),
): { discount: DiscountInput; finalMinor: number } | null {
  let best: { discount: DiscountInput; finalMinor: number } | null = null;
  for (const discount of discounts) {
    if (!isDiscountActive(discount, now)) continue;
    const finalMinor = applyDiscount(baseMinor, discount);
    if (finalMinor >= baseMinor) continue;
    if (!best || finalMinor < best.finalMinor) best = { discount, finalMinor };
  }
  return best;
}

export interface ComputePriceInput {
  unit: PriceUnit;
  baseMinor: number | null;
  currency?: string;
  discounts?: readonly DiscountInput[];
  now?: Date;
}

export function computePrice({
  unit,
  baseMinor,
  currency = 'INR',
  discounts = [],
  now = new Date(),
}: ComputePriceInput): PriceBreakdown {
  if (unit === 'ON_REQUEST' || baseMinor === null) {
    return { unit: 'ON_REQUEST', currency, baseMinor: null, finalMinor: null, discount: null };
  }
  const best = bestDiscount(baseMinor, discounts, now);
  if (!best) return { unit, currency, baseMinor, finalMinor: baseMinor, discount: null };
  const ends = toTime(best.discount.endsAt);
  return {
    unit,
    currency,
    baseMinor,
    finalMinor: best.finalMinor,
    discount: {
      type: best.discount.type,
      value: best.discount.value,
      label: best.discount.label ?? null,
      endsAt: ends === null ? null : new Date(ends).toISOString(),
      percentOff: percentOff(baseMinor, best.finalMinor),
    },
  };
}

/** `123400` → `₹1,234` · `123450` → `₹1,234.50` (paise shown only when present). */
export function formatMoney(minor: number, currency = 'INR', locale = 'en-IN'): string {
  const hasFraction = minor % 100 !== 0;
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: hasFraction ? 2 : 0,
    maximumFractionDigits: 2,
  }).format(minor / 100);
}

export const UNIT_SUFFIX: Readonly<Record<PriceUnit, string>> = {
  PER_SQ_FT: '/ sq ft',
  PER_SLAB: '/ slab',
  ON_REQUEST: '',
};
